/**
 * UI theme definitions for JDiskMark website.
 *
 * Theme files live in ./themes/. Linked themes include their palette
 * definition inline (exported as a named `palette` export from the
 * theme file).
 */
import { FEATURE_FLAGS } from './featureFlags';

// ── Theme imports ───────────────────────────────────────────────────────
import light        from './themes/light';
import dark         from './themes/dark';
import oldGlory     from './themes/oldGlory';
import sakura       from './themes/sakura';
import harvest      from './themes/harvest';
import trickOrTreat from './themes/trickOrTreat';
import yuletide     from './themes/yuletide';

const ALL_THEMES = [light, dark, oldGlory, sakura, harvest, trickOrTreat, yuletide];

// ── Feature-flag gate ───────────────────────────────────────────────────
const THEME_FLAG_MAP = {
  oldGlory:     'theme_oldGlory',
  sakura:       'theme_sakura',
  harvest:      'theme_harvest',
  trickOrTreat: 'theme_trickOrTreat',
  yuletide:     'theme_yuletide',
};

function isThemeEnabled(entry) {
  if (entry.enabled) return true;
  const flagKey = THEME_FLAG_MAP[entry.id];
  return flagKey ? FEATURE_FLAGS[flagKey] === true : false;
}

// ── CSS variable mapping ────────────────────────────────────────────────
const CSS_VAR_MAP = {
  headerBg:        '--header-bg',
  headerText:      '--header-text',
  accentColor:     '--accent-color',
  mainBg:          '--main-bg',
  mainText:        '--main-text',
  mainTextHeading: '--main-text-heading',
  mainHeroText:    '--main-hero-text',
  footerBg:        '--footer-bg',
  footerText:      '--footer-text',
  cardBg:          '--card-bg',
  cardText:        '--card-text',
  cardBorder:      '--card-bc',
  cardButtonBg:    '--card-button-bg',
  cardButtonText:  '--card-button-text',
  navLinkText:     '--nav-link-text',
  navLinkHover:    '--nav-link-text-hover',
  navLinkActive:   '--nav-link-text-active',
  listGroupBg:     '--list-group-bg',
  bmrkDetailBg:    '--card-bmrk-det-bg',
  bmrkMetricBg:    '--card-bmrk-met-bg',
  bmrkMetricText:  '--card-bmrk-met-text',
  bmrkMetricBorder:'--card-bmrk-met-bc',
  mainCardText:    '--main-card-text',
  navLinkFocus:    '--nav-link-text-focus',
  navEmailActive:  '--nav-email-active',
  homeProseLabel:  '--home-prose-label-text',
  homeProseLink:   '--home-prose-link-text',
  homeProseDlBtn:  '--home-prose-dl-btn-secondary-text',
  cardButtonBorder:'--card-button-bc',
  linkColor:       '--link-color',
  cardTextMuted:   '--card-text-muted',
  cardHeaderBg:    '--card-header-bg',
  cardHeaderText:  '--card-header-text',
};

/**
 * Ordered list of enabled themes for dropdown rendering.
 */
export const THEME_LIST = ALL_THEMES
  .filter(isThemeEnabled)
  .map((entry) => ({
    id:            entry.id,
    displayName:   entry.displayName,
    baseType:      entry.baseType,
    linkedPalette: entry.linkedPalette,
  }));

/**
 * Theme lookup map keyed by ID.
 */
export const THEMES = Object.fromEntries(
  ALL_THEMES.map((entry) => [entry.id, entry]),
);

/**
 * Apply a theme's CSS variables to the document root AND body.
 *
 * Base light/dark mode is controlled by the `data-theme` attribute (see
 * selectors like `[data-theme="dark"]`). Custom themes apply additional inline
 * CSS variable overrides so their values win over the base theme.
 */
export function applyThemeCssVars(themeId) {
  const theme = THEMES[themeId];
  if (!theme?.ui) return;

  const targets = [document.documentElement, document.body];
  for (const [key, cssVar] of Object.entries(CSS_VAR_MAP)) {
    const value = theme.ui[key];
    if (value != null) {
      for (const el of targets) el.style.setProperty(cssVar, value);
    }
  }
}

/**
 * Clear any inline theme CSS variable overrides from both html and body.
 */
export function clearThemeCssVars() {
  const targets = [document.documentElement, document.body];
  for (const cssVar of Object.values(CSS_VAR_MAP)) {
    for (const el of targets) el.style.removeProperty(cssVar);
  }
}

/**
 * Returns the baseType ('light' or 'dark') for a given theme ID.
 */
export function getThemeBaseType(themeId) {
  return THEMES[themeId]?.baseType ?? 'light';
}

/**
 * Returns the linked palette ID for a given theme, or null.
 */
export function getLinkedPalette(themeId) {
  return THEMES[themeId]?.linkedPalette ?? null;
}
